import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Control, UseFormRegister, UseFormSetValue, Controller, useWatch } from 'react-hook-form';
import { useQuery, useMutation, useApolloClient, gql } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import { format } from 'date-fns';
import Select from 'react-select';
import { GET_PACKAGE, GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';
import { GET_AIRLINES } from '../../graphql/queries/airline';
import { CREATE_PACKAGE, UPDATE_PACKAGE, UPLOAD_FILE } from '../../graphql/admin/mutations/package';
import { 
  PackageType,
  PriceType,
  CATEGORY_LABELS,
  PACKAGE_TYPE_LABELS,
  SERVICE_OPTIONS
} from '../../types/package';
import { Upload, X, Trash2, Sparkles } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUrl';
import { getApiEndpoint } from '../../utils/apiUrl';
import AiImageAnalyzer from '../../components/admin/AiImageAnalyzer';

interface MealPlan {
    breakfast: boolean;
    lunch: boolean;
    dinner: boolean;
}

interface DayItinerary {
  title: string;
  description: string;
  activities: string[];
  meals: MealPlan;
  accommodation: string;
}

interface PackageDeparture {
  departureDate: string;
  returnDate: string;
}

interface HotelItem {
  name: string;
  stars: string;
  image: string;
  address: string;
  amenities: string[];
  meals: string[];
  bookingUrl?: string;
}

interface Formula {
  name: string;
  priceType: PriceType;
  perPersonPrice?: number;
  roomPrices: {
    single: number;
    double: number;
    triple: number;
    quadruple: number;
    quintuple: number;
  };
  discountPercent?: number | null;
  services: string[];
  hotels: HotelItem[];
}

interface FormulaFieldsProps {
  index: number;
  register: UseFormRegister<FormFields>;
  remove: (index: number) => void;
  control: Control<FormFields>;
  setValue?: UseFormSetValue<FormFields>;
  onUploadFile?: (file: File, fieldName: string) => Promise<string>;
  errors?: { name?: { message?: string }; perPersonPrice?: { message?: string }; roomPrices?: { single?: { message?: string } } }[];
}

const FORMULA_ACCENTS = [
  { border: 'border-l-indigo-500', bg: 'bg-indigo-50/50', badge: 'bg-indigo-600 text-white' },
  { border: 'border-l-emerald-500', bg: 'bg-emerald-50/50', badge: 'bg-emerald-600 text-white' },
  { border: 'border-l-amber-500', bg: 'bg-amber-50/50', badge: 'bg-amber-600 text-white' },
  { border: 'border-l-violet-500', bg: 'bg-violet-50/50', badge: 'bg-violet-600 text-white' },
  { border: 'border-l-rose-500', bg: 'bg-rose-50/50', badge: 'bg-rose-600 text-white' },
];
const getFormulaAccent = (index: number) => FORMULA_ACCENTS[index % FORMULA_ACCENTS.length];

const DEFAULT_HOTEL: HotelItem = {
  name: '',
  stars: '3',
  image: '',
  address: '',
  amenities: [],
  meals: [],
  bookingUrl: ''
};

const HOTEL_COLORS = [
  'border-blue-300 bg-blue-50/40',
  'border-teal-300 bg-teal-50/40',
  'border-orange-300 bg-orange-50/40',
  'border-pink-300 bg-pink-50/40',
];

const HotelFieldGroup: React.FC<{
  formulaIndex: number;
  hotelIndex: number;
  register: UseFormRegister<FormFields>;
  control: Control<FormFields>;
  setValue?: UseFormSetValue<FormFields>;
  onUploadFile?: (file: File, fieldName: string) => Promise<string>;
  onRemove: () => void;
  canRemove: boolean;
}> = ({ formulaIndex, hotelIndex, register, control, setValue, onUploadFile, onRemove, canRemove }) => {
  const hotelImageUrl = useWatch({
    control,
    name: `formulas.${formulaIndex}.hotels.${hotelIndex}.image`
  });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const colorClass = HOTEL_COLORS[hotelIndex % HOTEL_COLORS.length];

  return (
    <div className={`border rounded-lg p-4 ${colorClass} relative`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-700">Hôtel {hotelIndex + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-800">
            Supprimer cet hôtel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'hôtel</label>
          <input
            type="text"
            {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.name`)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Nom de l'hôtel"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Étoiles</label>
          <select
            {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.stars`)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            {[1, 2, 3, 4, 5].map(s => (
              <option key={s} value={s.toString()}>{s} étoile{s > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
        <input
          type="text"
          {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.address`)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="Adresse de l'hôtel"
        />
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Lien de réservation (Booking)</label>
        <input
          type="url"
          {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.bookingUrl`)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="https://..."
        />
      </div>

      {/* Image */}
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
        <input type="hidden" {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.image`)} />
        {hotelImageUrl && (
          <div className="mb-2 inline-block">
            <img
              src={getImageUrl(hotelImageUrl)}
              alt="Aperçu"
              className="h-20 w-20 object-cover rounded border border-gray-200"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
        {setValue && onUploadFile ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                try {
                  const url = await onUploadFile(file, 'image');
                  setValue(`formulas.${formulaIndex}.hotels.${hotelIndex}.image`, url);
                } catch (err) {
                  console.error(err);
                } finally {
                  setUploading(false);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Téléversement...' : 'Choisir une image'}
            </button>
          </>
        ) : (
          <input
            type="text"
            {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.image`)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="URL de l'image"
          />
        )}
      </div>

      {/* Équipements */}
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Équipements</label>
        <Controller
          name={`formulas.${formulaIndex}.hotels.${hotelIndex}.amenities`}
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <div className="space-y-2">
              {(field.value || []).map((_: any, ai: number) => (
                <div key={ai} className="flex gap-2">
                  <input
                    type="text"
                    {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.amenities.${ai}`)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Équipement"
                  />
                  <button type="button" onClick={() => { const v = [...field.value]; v.splice(ai, 1); field.onChange(v); }} className="px-2 text-red-600 hover:text-red-800 text-sm">×</button>
                </div>
              ))}
              <button type="button" onClick={() => field.onChange([...(field.value || []), ''])} className="text-blue-600 hover:text-blue-800 text-sm">+ Ajouter un équipement</button>
            </div>
          )}
        />
      </div>

      {/* Repas */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Repas inclus</label>
        <Controller
          name={`formulas.${formulaIndex}.hotels.${hotelIndex}.meals`}
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <div className="space-y-2">
              {(field.value || []).map((_: any, mi: number) => (
                <div key={mi} className="flex gap-2">
                  <input
                    type="text"
                    {...register(`formulas.${formulaIndex}.hotels.${hotelIndex}.meals.${mi}`)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Repas"
                  />
                  <button type="button" onClick={() => { const v = [...field.value]; v.splice(mi, 1); field.onChange(v); }} className="px-2 text-red-600 hover:text-red-800 text-sm">×</button>
                </div>
              ))}
              <button type="button" onClick={() => field.onChange([...(field.value || []), ''])} className="text-blue-600 hover:text-blue-800 text-sm">+ Ajouter un repas</button>
            </div>
          )}
        />
      </div>
    </div>
  );
};

const FormulaFields: React.FC<FormulaFieldsProps> = ({ index, register, remove, control, setValue, onUploadFile, errors }) => {
  const fieldErrors = (errors as any)?.[index];
  const priceType = useWatch({ control, name: `formulas.${index}.priceType` });
  const hotels = useWatch({ control, name: `formulas.${index}.hotels` }) || [];
  const accent = getFormulaAccent(index);

  const addHotel = () => {
    if (setValue) {
      setValue(`formulas.${index}.hotels`, [...hotels, { ...DEFAULT_HOTEL }]);
    }
  };

  const removeHotel = (hotelIdx: number) => {
    if (setValue && hotels.length > 1) {
      setValue(`formulas.${index}.hotels`, hotels.filter((_: any, i: number) => i !== hotelIdx));
    }
  };

  return (
    <div className={`mb-6 overflow-hidden border border-gray-200 rounded-xl border-l-4 ${accent.border} ${accent.bg} shadow-sm`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200/80">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${accent.badge}`}>
          Formule {index + 1}
        </span>
        <button type="button" onClick={() => remove(index)} className="text-sm text-red-600 hover:text-red-800 font-medium">
          Supprimer cette formule
        </button>
      </div>
      <div className="p-5">
        {/* Nom */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nom de la formule <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register(`formulas.${index}.name`, { required: 'Le nom de la formule est obligatoire' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: Basic, Premium, VIP..."
          />
          {fieldErrors?.name?.message && <p className="mt-1 text-sm text-red-500">{fieldErrors.name.message}</p>}
        </div>

        {/* Type de prix */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Type de prix</label>
          <select {...register(`formulas.${index}.priceType`)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
            <option value="PER_PERSON">Prix par personne (même prix pour tous)</option>
            <option value="PER_ROOM">Prix par personne selon le type de chambre</option>
          </select>
          {priceType === 'PER_ROOM' && (
            <p className="mt-1 text-xs text-gray-500">Le type de chambre (single, double, etc.) influe sur le prix ; les montants saisis sont par personne.</p>
          )}
        </div>

        {/* Prix */}
        {priceType === 'PER_PERSON' ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prix par personne <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              {...register(`formulas.${index}.perPersonPrice`, {
                required: 'Le prix par personne est obligatoire',
                min: { value: 0, message: 'Le prix doit être positif ou nul' }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Prix en DA"
            />
            {fieldErrors?.perPersonPrice?.message && <p className="mt-1 text-sm text-red-500">{fieldErrors.perPersonPrice.message}</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <p className="col-span-full text-sm text-gray-600">Prix par personne pour chaque type de chambre (en DA) :</p>
            {(['single', 'double', 'triple', 'quadruple', 'quintuple'] as const).map((room) => (
              <div key={room}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chambre {room === 'single' ? '1 personne' : room === 'double' ? '2 personnes' : room === 'triple' ? '3 personnes' : room === 'quadruple' ? '4 personnes' : '5 personnes'} (prix par pers.) {room === 'single' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  {...register(`formulas.${index}.roomPrices.${room}`, room === 'single' ? {
                    required: 'Obligatoire', min: { value: 0, message: 'Positif' }
                  } : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Prix en DA par personne"
                />
              </div>
            ))}
          </div>
        )}

        {/* Réduction */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Réduction (%)</label>
          <input type="number" {...register(`formulas.${index}.discountPercent`)} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Ex: 10" min="0" max="100" />
        </div>

        {/* Services */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Services inclus</label>
          <Controller
            name={`formulas.${index}.services`}
            control={control}
            defaultValue={['']}
            render={({ field }) => (
              <div className="space-y-2">
                {(field.value as string[])?.map((_, si) => (
                  <div key={si} className="flex gap-2">
                    <input type="text" {...register(`formulas.${index}.services.${si}`)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md" placeholder="Ajouter un service" />
                    <button type="button" onClick={() => { const v = [...field.value]; v.splice(si, 1); field.onChange(v); }} className="px-3 py-2 text-red-600 hover:text-red-800">Supprimer</button>
                  </div>
                ))}
                <button type="button" onClick={() => field.onChange([...field.value, ''])} className="text-blue-600 hover:text-blue-800">+ Ajouter un service</button>
              </div>
            )}
          />
        </div>

        {/* Hôtels */}
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">
              {hotels.length <= 1 ? 'Hôtel' : 'Hôtels'}
            </h3>
            <button
              type="button"
              onClick={addHotel}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + Ajouter un hôtel
            </button>
          </div>
          <div className="space-y-4">
            {hotels.map((_: any, hIdx: number) => (
              <HotelFieldGroup
                key={hIdx}
                formulaIndex={index}
                hotelIndex={hIdx}
                register={register}
                control={control}
                setValue={setValue}
                onUploadFile={onUploadFile}
                onRemove={() => removeHotel(hIdx)}
                canRemove={hotels.length > 1}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingState: React.FC = () => (
  <AdminLayout>
    <div className="flex items-center justify-center h-screen">
      <div className="text-lg">Chargement des données...</div>
    </div>
  </AdminLayout>
);

const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <AdminLayout>
    <div className="text-red-600">
      Erreur lors du chargement du package: {message}
    </div>
  </AdminLayout>
);

const useAgencyData = () => {
  const [agencyId, setAgencyId] = React.useState<number | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const GET_AGENCY = gql`
    query GetAgency($name: String!) {
      agency(name: $name) {
        id
        name
        logo
        description
        address
        phone
        email
        website
        facebook
        instagram
        tiktok
        theme {
          primaryColor
          secondaryColor
          textColor
        }
      }
    }
  `;

  const { loading, error, data } = useQuery(GET_AGENCY, {
    variables: { name: user.agencyName },
    skip: !user.agencyName,
    onCompleted: (data) => {
      if (data?.agency?.id) {
        setAgencyId(data.agency.id);
        // Mettre à jour le user dans le localStorage avec l'agencyId
        const updatedUser = { ...user, agencyId: data.agency.id };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    }
  });

  return { loading, error, agencyId, user };
};

const loadExistingPackage = (packageData: any) => {
  try {
    if (packageData) {
      reset({
        ...packageData,
        commission: packageData.commission || 10, // Commission partenaire (10% par défaut)
        departures: packageData.departures.map((departure: any) => ({
          departureDate: format(new Date(departure.departureDate), 'yyyy-MM-dd'),
          returnDate: format(new Date(departure.returnDate), 'yyyy-MM-dd')
        })),
        formulas: packageData.formulas.map((formula: any) => {
          const hotels = formula.hotels || (formula.hotel ? [formula.hotel] : []);
          return {
            name: formula.name,
            priceType: formula.priceType || PriceType.PER_PERSON,
            perPersonPrice: formula.perPersonPrice || 0,
            roomPrices: formula.roomPrices || {
              single: 0, double: 0, triple: 0, quadruple: 0, quintuple: 0
            },
            discountPercent: formula.discountPercent || 0,
            services: formula.services || [''],
            hotels: hotels.length > 0 ? hotels.map((h: any) => ({
              name: h.name || '',
              stars: h.stars || '3',
              image: h.image || '',
              address: h.address || '',
              amenities: h.amenities || [],
              meals: h.meals || [],
              bookingUrl: h.bookingUrl || ''
            })) : [{ name: '', stars: '3', image: '', address: '', amenities: [], meals: [], bookingUrl: '' }]
          };
        }),
        itinerary: packageData.itinerary.map((day: any) => ({
          title: day.title || '',
          description: day.description || '',
          activities: day.activities || [''],
  meals: {
            breakfast: Boolean(day.meals.breakfast),
            lunch: Boolean(day.meals.lunch),
            dinner: Boolean(day.meals.dinner)
          },
          accommodation: day.accommodation || ''
        }))
      });
    }
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error);
    alert('Erreur lors du chargement des données du package');
  }
};

const validateFormData = (data: FormFields): boolean => {
  if (!data.formulas || data.formulas.length === 0) {
    alert('Vous devez ajouter au moins une formule');
    return false;
  }

  if (!data.itinerary || data.itinerary.length === 0) {
    alert('Vous devez ajouter au moins un jour à l\'itinéraire');
    return false;
  }

  if (new Date(data.returnDate) < new Date(data.departureDate)) {
    alert('La date de retour doit être après la date de départ');
    return false;
  }

  return true;
};

const formatDate = (dateValue: string | number | Date | null): string => {
  if (!dateValue) return '';

  try {
    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'number') {
      // Pour les timestamps en millisecondes
      date = new Date(dateValue);
    } else if (typeof dateValue === 'string' && !isNaN(Number(dateValue))) {
      // Si c'est une chaîne numérique (timestamp)
      date = new Date(Number(dateValue));
    } else {
      // Pour les chaînes de caractères de date
      const cleanDateString = dateValue.toString().replace(/Z|(\+|-)\d{2}:\d{2}$/, '');
      date = new Date(cleanDateString);
    }

    if (isNaN(date.getTime())) {
      console.warn('Date invalide:', dateValue);
      return '';
    }

    // Ajuster pour le fuseau horaire local
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - (offset * 60 * 1000));

    return date.toISOString().split('T')[0];

  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return '';
  }
};

const defaultFormula: Formula = {
  name: '',
  priceType: PriceType.PER_PERSON,
  perPersonPrice: 0,
  roomPrices: {
    single: 0,
    double: 0,
    triple: 0,
    quadruple: 0,
    quintuple: 0
  },
  discountPercent: 0,
  services: [],
  hotels: [{
    name: '',
    stars: '3',
    image: '',
    address: '',
    amenities: [],
    meals: []
  }]
};

const defaultItineraryDay = {
  title: '',
  description: '',
  activities: [''],
  meals: {
    breakfast: false,
    lunch: false,
    dinner: false
  },
  accommodation: ''
};

const cleanFormData = (data: FormFields) => {
  const formattedFormulas = data.formulas.map(formula => ({
    ...formula,
    perPersonPrice: formula.perPersonPrice ? Number(formula.perPersonPrice) : null,
    roomPrices: {
      single: Number(formula.roomPrices.single),
      double: Number(formula.roomPrices.double),
      triple: Number(formula.roomPrices.triple),
      quadruple: Number(formula.roomPrices.quadruple),
      quintuple: Number(formula.roomPrices.quintuple)
    },
    discountPercent: formula.discountPercent ? Math.round(Number(formula.discountPercent)) : null,
    services: formula.services.filter(service => service.trim() !== ''),
    hotels: formula.hotels.map(h => ({
      name: h.name,
      stars: h.stars,
      image: h.image,
      address: h.address,
      amenities: (h.amenities || []).filter(a => a.trim() !== ''),
      meals: (h.meals || []).filter(m => m.trim() !== ''),
      bookingUrl: h.bookingUrl?.trim() || undefined
    }))
  }));

  return {
    title: data.title,
    description: data.description,
    image: data.image,
    groupSize: data.groupSize,
    category: data.category,
    type: data.type,
    departure: data.departure,
    commission: data.commission ? Number(data.commission) : 10, // Commission partenaire (10% par défaut)
    departures: data.departures.map(dep => ({
      departureDate: dep.departureDate ? new Date(dep.departureDate).toISOString() : new Date().toISOString(),
      returnDate: dep.returnDate ? new Date(dep.returnDate).toISOString() : new Date().toISOString()
    })),
    highlights: data.highlights.filter(item => item.trim() !== ''),
    included: data.included.filter(item => item.trim() !== ''),
    excluded: data.excluded.filter(item => item.trim() !== ''),
    cities: data.cities.filter(item => item.trim() !== ''),
    countries: data.countries.filter(item => item.trim() !== ''),
    formulas: formattedFormulas,
    itinerary: data.itinerary.map((day, index) => ({
      dayNumber: index + 1,
      title: day.title,
      description: day.description,
      activities: day.activities.filter(activity => activity.trim() !== ''),
      accommodation: day.accommodation,
      image: '',
      meals: {
        breakfast: day.meals.breakfast,
        lunch: day.meals.lunch,
        dinner: day.meals.dinner
      }
    }))
  };
};

interface FormFields {
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: PackageType;
  departure: string;
  departures: PackageDeparture[];
  highlights: string[];
  included: string[];
  excluded: string[];
  cities: string[];
  countries: string[];
  formulas: Formula[];
  itinerary: DayItinerary[];
  commission?: number;
  programPdf?: string;
  /** Chaîne pour <select> : id ou "" */
  airlineId?: string;
}

const formatDepartureDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return '';
  }
};

const appendEmptyString = (append: (value: any) => void) => {
  append('');
};

export default function PackageFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const client = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialiser avec un jour par défaut
  const defaultValues: FormFields = {
    title: '',
    description: '',
    image: '',
    groupSize: '',
    category: 'OMRA',
    departure: '',
    departures: [{
      departureDate: formatDate(new Date()),
      returnDate: formatDate(new Date())
    }],
    highlights: [''],
    included: [''],
    excluded: [''],
    cities: [''],
    countries: [''],
    formulas: [{
      name: '',
      priceType: PriceType.PER_PERSON,
      perPersonPrice: 0,
      roomPrices: {
        single: 0,
        double: 0,
        triple: 0,
        quadruple: 0,
        quintuple: 0
      },
      discountPercent: 0,
      services: [],
      hotels: [{
        name: '',
        stars: '3',
        image: '',
        address: '',
        amenities: [],
        meals: [],
        bookingUrl: ''
      }]
    }],
    type: 'B2C' as PackageType,
    commission: 10, // Commission partenaire par défaut (10%)
    programPdf: '',
    airlineId: ''
  };

  const { register, control, handleSubmit, formState: { errors }, reset, watch, setValue, getValues } = useForm<FormFields>({
    defaultValues
  });

  // Configuration des champs de tableau pour formules et itinéraire
  const { fields: formulaFields, append: appendFormula, remove: removeFormula } = useFieldArray<FormFields>({
    control,
    name: "formulas"
  });

  const { fields: itineraryFields, append: appendItinerary, remove: removeItinerary } = useFieldArray<FormFields>({
    control,
    name: "itinerary"
  });

  // Watch itinerary pour que les activités par jour se mettent à jour à l'écran (setValue déclenche le re-render)
  const watchedItinerary = watch("itinerary");

  // Ajout des useFieldArray pour chaque liste
  const { fields: highlightFields, append: appendHighlight, remove: removeHighlight } = useFieldArray<FormFields>({
    control,
    name: "highlights"
  });

  const { fields: includedFields, append: appendIncluded } = useFieldArray<FormFields>({
    control,
    name: "included"
  });

  const { fields: excludedFields, append: appendExcluded } = useFieldArray<FormFields>({
    control,
    name: "excluded"
  });

  const { fields: citiesFields, append: appendCity, remove: removeCity } = useFieldArray<FormFields>({
    control,
    name: "cities"
  });

  const { fields: countriesFields, append: appendCountry, remove: removeCountry } = useFieldArray<FormFields>({
    control,
    name: "countries"
  });

  const { fields: amenitiesFields, append: appendAmenity, remove: removeAmenity } = useFieldArray<FormFields>({
    control,
    name: "formulas.0.hotels.0.amenities" as any
  });

  // Ajouter useFieldArray pour les dates de départ
  const { 
    fields: departureFields, 
    append: appendDeparture, 
    remove: removeDeparture 
  } = useFieldArray<FormFields>({
    control,
    name: "departures"
  });

  // Helpers pour gérer les activités par jour (chaque jour a son propre tableau d'activités)
  const appendActivityForDay = (dayIndex: number) => {
    const current = getValues(`itinerary.${dayIndex}.activities`) ?? [''];
    setValue(`itinerary.${dayIndex}.activities`, [...current, '']);
  };
  const removeActivityForDay = (dayIndex: number, activityIndex: number) => {
    const current = getValues(`itinerary.${dayIndex}.activities`) ?? [''];
    if (current.length <= 1) return; // garder au moins un champ
    setValue(
      `itinerary.${dayIndex}.activities`,
      current.filter((_, i) => i !== activityIndex)
    );
  };

  // Query pour récupérer les données du package (network-first en édition pour éviter cache périmé)
  const { loading: queryLoading, error: queryError, data: packageData } = useQuery(GET_PACKAGE, {
    variables: { id: parseInt(id || '0') },
    skip: !id,
    fetchPolicy: id ? 'network-first' : 'cache-first',
    onError: (error) => {
      console.error('Erreur lors du chargement du package:', error);
    }
  });

  const { data: airlinesData } = useQuery(GET_AIRLINES);

  // Mutation pour créer un package
  const [createPackage, { loading: createLoading }] = useMutation(CREATE_PACKAGE, {
    onCompleted: () => {
      alert('Package créé avec succès!');
      navigate('/admin/packages');
    },
    onError: (error) => {
      alert(`Erreur lors de la création : ${error.message}`);
    }
  });

  // Mutation pour mettre à jour un package
  const [updatePackage, { loading: updateLoading }] = useMutation(UPDATE_PACKAGE, {
    onCompleted: () => {
      alert('Package mis à jour avec succès!');
      navigate('/admin/packages');
    },
    onError: (error) => {
      alert(`Erreur lors de la mise à jour : ${error.message}`);
    }
  });

  // Vérifier si le package a des réservations
  const hasBookings = packageData?.package?.bookings && packageData.package.bookings.length > 0;

  // Ajouter la mutation pour l'upload de fichiers
  const [uploadFile] = useMutation(UPLOAD_FILE);

  const { loading, error, agencyId, user } = useAgencyData();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [showAiPackage, setShowAiPackage] = useState(false);
  const [showAiItinerary, setShowAiItinerary] = useState(false);
  const [aiRemaining, setAiRemaining] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(getApiEndpoint('/api/ai/usage'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => { if (json.remaining != null) setAiRemaining(json.remaining); })
      .catch(() => {});
  }, []);
  const [programPdfFile, setProgramPdfFile] = useState<File | null>(null);
  const programPdfInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyAiPackageData = (data: Record<string, any>) => {
    if (data.title) setValue('title', data.title);
    if (data.description) setValue('description', data.description);
    if (data.groupSize) setValue('groupSize', data.groupSize);
    if (data.category && Object.keys(CATEGORY_LABELS).includes(data.category)) {
      setValue('category', data.category);
    }
    if (data.departure) setValue('departure', data.departure);
    if (Array.isArray(data.departures) && data.departures.length > 0) {
      setValue('departures', data.departures.map((d: any) => ({
        departureDate: d.departureDate || formatDate(new Date()),
        returnDate: d.returnDate || formatDate(new Date()),
      })));
    }
    if (Array.isArray(data.highlights) && data.highlights.length > 0) {
      setValue('highlights', data.highlights);
    }
    if (Array.isArray(data.included) && data.included.length > 0) {
      setValue('included', data.included);
    }
    if (Array.isArray(data.excluded) && data.excluded.length > 0) {
      setValue('excluded', data.excluded);
    }
    if (Array.isArray(data.cities) && data.cities.length > 0) {
      setValue('cities', data.cities);
    }
    if (Array.isArray(data.countries) && data.countries.length > 0) {
      setValue('countries', data.countries);
    }
    if (Array.isArray(data.formulas) && data.formulas.length > 0) {
      setValue('formulas', data.formulas.map((f: any) => {
        const aiHotels = f.hotels || (f.hotel ? [f.hotel] : []);
        return {
          name: f.name || '',
          priceType: f.priceType === 'PER_ROOM' ? PriceType.PER_ROOM : PriceType.PER_PERSON,
          perPersonPrice: f.perPersonPrice || 0,
          roomPrices: {
            single: f.roomPrices?.single || 0,
            double: f.roomPrices?.double || 0,
            triple: f.roomPrices?.triple || 0,
            quadruple: f.roomPrices?.quadruple || 0,
            quintuple: f.roomPrices?.quintuple || 0,
          },
          discountPercent: f.discountPercent || 0,
          services: f.services || [],
          hotels: aiHotels.length > 0 ? aiHotels.map((h: any) => ({
            name: h.name || '',
            stars: h.stars || '3',
            image: h.image || '',
            address: h.address || '',
            amenities: h.amenities || [],
            meals: h.meals || [],
            bookingUrl: h.bookingUrl || '',
          })) : [{ name: '', stars: '3', image: '', address: '', amenities: [], meals: [], bookingUrl: '' }],
        };
      }));
    }
  };

  const applyAiItineraryData = (data: Record<string, any>) => {
    if (Array.isArray(data.itinerary) && data.itinerary.length > 0) {
      setValue('itinerary', data.itinerary.map((day: any) => ({
        title: day.title || '',
        description: day.description || '',
        activities: Array.isArray(day.activities) && day.activities.length > 0 ? day.activities : [''],
        meals: {
          breakfast: day.meals?.breakfast ?? false,
          lunch: day.meals?.lunch ?? false,
          dinner: day.meals?.dinner ?? false,
        },
        accommodation: day.accommodation || '',
      })));
    }
  };

  const onSubmit = async (data: FormFields) => {
    try {
      setIsSubmitting(true);
      
      
      // Upload de l'image si modifiée
      let imageUrl = data.image;
      if (imageFile) {
        imageUrl = await uploadFileToServer(imageFile, 'image');
      }

      // Upload du PDF programme si fourni
      let programPdfUrl: string | undefined = data.programPdf || undefined;
      if (programPdfFile) {
        programPdfUrl = await uploadFileToServer(programPdfFile, 'program_pdf');
      }

      // Nettoyer les données avant l'envoi
      const cleanedData = {
        // Supprimer les champs non autorisés (__typename, id, agency)
        title: data.title,
        description: data.description,
        image: imageUrl,
        groupSize: data.groupSize,
        category: data.category,
        type: data.type,
        departure: data.departure,
        departures: data.departures.map(dep => ({
          departureDate: new Date(dep.departureDate).toISOString(),
          returnDate: new Date(dep.returnDate).toISOString()
        })),
        highlights: data.highlights.filter(item => item.trim() !== ''),
        included: data.included.filter(item => item.trim() !== ''),
        excluded: data.excluded.filter(item => item.trim() !== ''),
        cities: data.cities.filter(item => item.trim() !== ''),
        countries: data.countries.filter(item => item.trim() !== ''),
        formulas: data.formulas.map(formula => ({
          name: formula.name,
          priceType: formula.priceType,
          perPersonPrice: formula.perPersonPrice ? parseFloat(formula.perPersonPrice.toString()) : null,
          roomPrices: {
            single: Number(formula.roomPrices.single),
            double: Number(formula.roomPrices.double),
            triple: Number(formula.roomPrices.triple),
            quadruple: Number(formula.roomPrices.quadruple),
            quintuple: Number(formula.roomPrices.quintuple)
          },
          discountPercent: formula.discountPercent ? parseInt(formula.discountPercent.toString()) : null,
          services: formula.services.filter(service => service.trim() !== ''),
          hotels: formula.hotels.map(h => ({
            name: h.name,
            stars: h.stars,
            image: h.image,
            address: h.address,
            amenities: (h.amenities || []).filter(a => a.trim() !== ''),
            meals: (h.meals || []).filter(m => m.trim() !== ''),
            bookingUrl: h.bookingUrl?.trim() || undefined
          }))
        })),
        // Renommer 'itinerary' en 'itineraryData' pour correspondre au type PackageInput
        itineraryData: data.itinerary.map((day, index) => {
          
          // S'assurer que tous les champs requis sont présents
          const processedDay = {
            dayNumber: index + 1,
            title: day.title || '',
            description: day.description || '',
            activities: (day.activities || []).filter(activity => activity.trim() !== ''),
            accommodation: day.accommodation || '',
            meals: {
              breakfast: Boolean(day.meals?.breakfast),
              lunch: Boolean(day.meals?.lunch),
              dinner: Boolean(day.meals?.dinner)
            }
          };
          
          return processedDay;
        }),
        ...(programPdfUrl !== undefined ? { programPdf: programPdfUrl } : {}),
        commission:
          data.commission != null && !Number.isNaN(Number(data.commission))
            ? Number(data.commission)
            : 10,
        airlineId: (() => {
          const v = data.airlineId;
          if (v === '' || v === undefined || v === null) return null;
          const n = parseInt(String(v), 10);
          return Number.isNaN(n) ? null : n;
        })()
      };


      const userInput = {
        email: user.email,
        agencyName: user.agencyName,
        id: user.id,
        agencyId: user.agencyId
      };

      if (id) {
        await updatePackage({
          variables: {
            id: Number(id),
            input: cleanedData,
            user: userInput
          },
          refetchQueries: [
            { query: GET_PACKAGE, variables: { id: Number(id) } },
            ...(user?.agencyName ? [{ query: GET_AGENCY_PACKAGES, variables: { agencyName: user.agencyName } }] : [])
          ],
          awaitRefetchQueries: true
        });
      } else {
        await createPackage({
          variables: {
            input: cleanedData,
            user: userInput
          }
        });
      }

      navigate('/admin/packages');
    } catch (error) {
      console.error('Erreur détaillée lors de la soumission:', error);
      alert('Une erreur est survenue lors de la sauvegarde');
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadFileToServer = async (file: File, fieldName: string): Promise<string> => {
    try {
      // Compresser l'image si nécessaire
      let fileToUpload = file;
      if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        };
        
        try {
          const imageCompression = await import('browser-image-compression');
          fileToUpload = await imageCompression.default(file, options);
        } catch (compressError) {
          console.error('Erreur lors de la compression:', compressError);
        }
      }
      
      // Convertir en base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(fileToUpload);
      });
      
      // Récupérer l'ID du package depuis l'URL
      const packageId = id ? parseInt(id, 10) : undefined;
      
      // S'assurer que les variables sont bien définies
      const variables = {
        file: base64,
        agencyName: user.agencyName || '',
        fieldName,
        ...(packageId && { entityId: packageId }) // N'ajouter entityId que s'il existe
      };
      
      
      const { data } = await uploadFile({
        variables
      });
      
      if (!data?.uploadFile?.url) {
        throw new Error('URL de l\'image non reçue');
      }
      
      return data.uploadFile.url;
    } catch (error) {
      console.error('Erreur détaillée lors de l\'upload:', error);
      throw error;
    }
  };

  // Charger les données du package dans le formulaire (une fois les données dispo, reset complet)
  useEffect(() => {
    if (!id || !packageData?.package) return;
    const pkg = packageData.package;
    if (String(pkg.id) !== String(id)) return;

    const formattedData = {
        ...pkg,
        image: pkg.image || '',
        category: pkg.category && Object.keys(CATEGORY_LABELS).includes(pkg.category) ? pkg.category : 'OMRA',
        departures: pkg.departures.map((departure: any) => {
          const departureDate = formatDate(departure.departureDate);
          const returnDate = formatDate(departure.returnDate);
          
          return {
            departureDate,
            returnDate
          };
        }),
        formulas: pkg.formulas.map((formula: any) => {
          const hotels = formula.hotels || (formula.hotel ? [formula.hotel] : []);
          return {
            name: formula.name,
            priceType: formula.priceType,
            perPersonPrice: formula.perPersonPrice,
            roomPrices: formula.roomPrices,
            discountPercent: formula.discountPercent,
            services: formula.services || [''],
            hotels: hotels.length > 0 ? hotels.map((h: any) => ({
              name: h.name || '',
              stars: h.stars || '3',
              image: h.image || '',
              address: h.address || '',
              amenities: h.amenities || [],
              meals: h.meals || [],
              bookingUrl: h.bookingUrl || ''
            })) : [{ name: '', stars: '3', image: '', address: '', amenities: [], meals: [], bookingUrl: '' }]
          };
        }),
        itinerary: pkg.itinerary.map((day: any) => ({
          title: day.title,
          description: day.description,
          activities: day.activities || [''],
          meals: {
            breakfast: Boolean(day.meals.breakfast),
            lunch: Boolean(day.meals.lunch),
            dinner: Boolean(day.meals.dinner)
          },
          accommodation: day.accommodation
        })),
        programPdf: pkg.programPdf || '',
        commission: pkg.commission != null ? Number(pkg.commission) : 10,
        airlineId: pkg.airlineId != null ? String(pkg.airlineId) : ''
      };

    setProgramPdfFile(null);
    setImagePreview(null);
    setImageFile(null);
    reset(formattedData, { keepDefaultValues: false });
  }, [id, packageData, reset]);

  // Afficher un état de chargement pendant la récupération des données de l'agence
  if (loading) return <div>Chargement des données de l'agence...</div>;
  if (error) return <div>Erreur: {error.message}</div>;

  // Afficher un message de chargement
  if (queryLoading) {
    return <LoadingState />;
  }

  // Afficher les erreurs de requête
  if (queryError) {
    return <ErrorState message={queryError.message} />;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-8">
          {id ? 'Modifier le package' : 'Nouveau package'}
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 pb-28">
          {/* Informations de base */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium">Informations générales</h2>
              <button
                type="button"
                onClick={() => setShowAiPackage(true)}
                disabled={aiRemaining === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${aiRemaining === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
              >
                <Sparkles className="w-4 h-4" />
                {aiRemaining == null ? 'Remplir par AI' : aiRemaining === 0 ? 'AI épuisée (0/5)' : `Remplir par AI (${aiRemaining}/5)`}
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register("title", { required: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {errors.title && <span className="text-red-500">Ce champ est requis</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register("description", { required: true })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {errors.description && <span className="text-red-500">Ce champ est requis</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image principale <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center space-x-4">
                  {(imagePreview || watch('image')) && (
                    <div className="w-32 h-24 border rounded-md overflow-hidden relative">
                      <img
                        src={imagePreview || getImageUrl(watch('image'))}
                        alt="Package preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setImageFile(null);
                          setValue('image', '');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choisir une image
                    </button>
                    <p className="mt-1 text-xs text-gray-500">
                      Format recommandé: JPG ou PNG, 16:9 ratio
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF du programme (optionnel)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Si vous ajoutez un PDF, il sera proposé au téléchargement. Sinon, le PDF est généré automatiquement à partir des informations du package.
                </p>
                <input type="hidden" {...register('programPdf')} />
                <input
                  ref={programPdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setProgramPdfFile(file);
                      setValue('programPdf', ''); // sera remplacé à l'envoi
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => programPdfInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  {programPdfFile ? programPdfFile.name : (watch('programPdf') ? 'Remplacer le PDF' : 'Choisir un PDF du programme')}
                </button>
                {programPdfFile && (
                  <span className="ml-2 text-sm text-green-600">Nouveau PDF sélectionné</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu de départ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("departure", { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.departure && <span className="text-red-500">Ce champ est requis</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taille du groupe <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("groupSize", { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.groupSize && <span className="text-red-500">Ce champ est requis</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("category", { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {errors.category && <span className="text-red-500">Ce champ est requis</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de package <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("type", { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Sélectionnez un type</option>
                    {Object.entries(PACKAGE_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <span className="text-red-500">Le type de package est requis</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compagnie aérienne (optionnel)
                </label>
                <select
                  {...register('airlineId')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                >
                  <option value="">— Aucune —</option>
                  {(airlinesData?.airlines ?? []).map((a: { id: number; name: string; code?: string | null }) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                      {a.code ? ` (${a.code})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Liste gérée en base de données (nom et logo affichés sur le marketplace et le site agence).
                </p>
              </div>

              <div className="hidden grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission partenaire (%)
                  </label>
                  <input
                    type="number"
                    {...register("commission", { 
                      required: false,
                      min: 0,
                      max: 100,
                      valueAsNumber: true
                    })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                      hasBookings ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="10"
                    step="0.1"
                    disabled={hasBookings}
                  />
                  {hasBookings ? (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ La commission ne peut pas être modifiée car ce package a déjà des réservations
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Pourcentage de commission versé au partenaire qui réserve ce package
                    </p>
                  )}
                </div>
              </div>

              {/* Section des dates de départ */}
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium">Dates de départ</h2>
                  <button
                    type="button"
                    onClick={() => appendDeparture({ departureDate: '', returnDate: '' })}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    + Ajouter une date
                  </button>
                </div>

                {departureFields.map((field, index) => (
                  <div key={field.id} className="mb-4 p-4 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date de départ <span className="text-red-500">*</span>
                  </label>
                  <input
                          type="date"
                          {...register(`departures.${index}.departureDate` as const, { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          defaultValue={field.departureDate}
                  />
                        {errors.departures?.[index]?.departureDate && (
                          <span className="text-red-500">Ce champ est requis</span>
                        )}
                </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date de retour <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          {...register(`departures.${index}.returnDate` as const, {
                            required: true,
                            validate: (value) => {
                              const departureDate = watch(`departures.${index}.departureDate`);
                              return new Date(value) >= new Date(departureDate) ||
                                'La date de retour doit être après la date de départ';
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {errors.departures?.[index]?.returnDate && (
                          <span className="text-red-500">
                            {errors.departures[index]?.returnDate?.message || 'Ce champ est requis'}
                          </span>
                        )}
              </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDeparture(index)}
                      className="mt-2 text-red-600 hover:text-red-800"
                    >
                      Supprimer cette date
                    </button>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* Points forts (ligne à part) */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium mb-6">Points forts</h2>
            <div className="space-y-2">
              {highlightFields.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <input
                    type="text"
                    {...register(`highlights.${index}` as const)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => removeHighlight(index)}
                    title="Supprimer ce point fort"
                    className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 text-sm font-medium"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => appendEmptyString(appendHighlight)}
                className="mt-2 text-blue-600 hover:text-blue-800"
              >
                + Ajouter un point fort
              </button>
            </div>
          </div>

          {/* Inclus et Non inclus (même ligne) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium mb-6">Inclus</h2>
              <div className="space-y-2">
                {includedFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <input
                      type="text"
                      {...register(`included.${index}` as const)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeIncluded(index)}
                      title="Supprimer cet élément"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => appendIncluded("")}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  + Ajouter un élément inclus
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium mb-6">Non inclus</h2>
              <div className="space-y-2">
                {excludedFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <input
                      type="text"
                      {...register(`excluded.${index}` as const)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeExcluded(index)}
                      title="Supprimer cet élément"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => appendExcluded("")}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  + Ajouter un élément non inclus
                </button>
              </div>
            </div>
          </div>

          {/* Villes et Pays */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium mb-6">Villes visitées</h2>
              <div className="space-y-2">
                {citiesFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <input
                      type="text"
                      {...register(`cities.${index}` as const)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeCity(index)}
                      title="Supprimer cette ville"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => appendCity("")}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  + Ajouter une ville
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium mb-6">Pays visités</h2>
              <div className="space-y-2">
                {countriesFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <input
                      type="text"
                      {...register(`countries.${index}` as const)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeCountry(index)}
                      title="Supprimer ce pays"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => appendCountry("")}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  + Ajouter un pays
                </button>
              </div>
            </div>
          </div>

          {/* Section des formules */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">Formules <span className="text-red-500">*</span></h2>
                          <button
                            type="button"
                onClick={() => appendFormula(defaultFormula)}
                className="text-blue-600 hover:text-blue-800"
                          >
                + Ajouter une formule
                          </button>
                </div>

            {formulaFields.map((field, index) => (
              <FormulaFields
                key={field.id}
                index={index}
                register={register}
                remove={removeFormula}
                control={control}
                setValue={setValue}
                onUploadFile={uploadFileToServer}
                errors={errors?.formulas}
              />
            ))}
          </div>

          {/* Itinéraire */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium">Itinéraire <span className="text-red-500">*</span></h2>
              <button
                type="button"
                onClick={() => setShowAiItinerary(true)}
                disabled={aiRemaining === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${aiRemaining === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
              >
                <Sparkles className="w-4 h-4" />
                {aiRemaining == null ? 'Remplir par AI' : aiRemaining === 0 ? 'AI épuisée (0/5)' : `Remplir par AI (${aiRemaining}/5)`}
              </button>
            </div>
            
            {itineraryFields.map((field, index) => (
              <div key={field.id} className="mb-6 p-4 border border-gray-200 rounded-lg">
                <div className="grid grid-cols-1 gap-4">
                  {/* Titre de la journée */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jour {index + 1} - Titre de la journée
                    </label>
                    <input
                      type="text"
                      {...register(`itinerary.${index}.title`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Ex: Découverte de la ville"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      {...register(`itinerary.${index}.description`)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Description détaillée de la journée"
                    />
                  </div>

                  {/* Activités */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Activités
                    </label>
                    <div className="space-y-2">
                      {(watchedItinerary?.[index]?.activities ?? field.activities ?? ['']).map((_, activityIndex) => (
                        <div key={activityIndex} className="flex gap-2">
                          <input
                            type="text"
                            {...register(`itinerary.${index}.activities.${activityIndex}`)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Ajouter une activité"
                          />
                          <button
                            type="button"
                            onClick={() => removeActivityForDay(index, activityIndex)}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => appendActivityForDay(index)}
                        className="mt-2 text-blue-600 hover:text-blue-800"
                      >
                        + Ajouter une activité
                      </button>
                    </div>
                  </div>

                  {/* Repas inclus */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repas inclus
                    </label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          {...register(`itinerary.${index}.meals.breakfast`)}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2">Petit-déjeuner</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          {...register(`itinerary.${index}.meals.lunch`)}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2">Déjeuner</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          {...register(`itinerary.${index}.meals.dinner`)}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2">Dîner</span>
                      </label>
                      </div>
                    </div>
                  </div>

                  {/* Hébergement */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hébergement
                    </label>
                    <input
                      type="text"
                      {...register(`itinerary.${index}.accommodation`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Nom de l'hébergement"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItinerary(index)}
                    className="mt-4 text-red-600 hover:text-red-800"
                  >
                    Supprimer ce jour
                  </button>
                </div>
              </div>
            ))}

            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => appendItinerary(defaultItineraryDay)}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter un nouveau jour
              </button>
            </div>
          </div>

          {/* Barre fixe des boutons de soumission */}
          <div className="fixed bottom-0 left-0 md:left-64 right-0 z-30 bg-white border-t border-gray-200 shadow-lg py-4 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/admin/packages')}
                className="px-4 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createLoading || updateLoading}
                className="px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {createLoading || updateLoading ? 'Enregistrement...' : (id ? 'Mettre à jour' : 'Créer le package')}
              </button>
            </div>
          </div>
        </form>

        {showAiPackage && (
          <AiImageAnalyzer
            mode="package"
            onResult={(data) => {
              applyAiPackageData(data);
              setAiRemaining((prev) => (prev != null ? Math.max(0, prev - 1) : prev));
            }}
            onClose={() => setShowAiPackage(false)}
          />
        )}
        {showAiItinerary && (
          <AiImageAnalyzer
            mode="itinerary"
            onResult={(data) => {
              applyAiItineraryData(data);
              setAiRemaining((prev) => (prev != null ? Math.max(0, prev - 1) : prev));
            }}
            onClose={() => setShowAiItinerary(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
}